import { Reducer } from "redux";
import produce from "immer";
import { IChatbotState, initialChatbotState } from "./chatbot.types";

import {
  FETCH_CONTACT_LIST_PROGRESS,
  FETCH_CONTACT_LIST_SUCCESS,
  FETCH_CONTACT_LIST_FAILED,
  FETCH_CONTACT_LIST_WHATSAPP_NOT_SCANNED,
  FETCH_CONTACT_LIST_WHATSAPP_SERVICE_OFF,
  CLEAR_CONTACT_STATE,
  FETCH_ACTION_EVENT_PROGRESS,
  FETCH_ACTION_EVENT_SUCCESS,
  FETCH_ACTION_EVENT_FAILED,
  CLEAR_ACTION_EVENT_STATE,
  FETCH_WHATSAPP_MSG_MEDIA_HISTORY_PROGRESS,
  FETCH_WHATSAPP_MSG_MEDIA_HISTORY_SUCCESS,
  FETCH_WHATSAPP_MSG_MEDIA_HISTORY_FAILED,
} from "./chatbotActions";
import { IStoreState } from "../initialStoreState";
import { LoadState } from "../../constants/enums";

const chatbotReducer: Reducer<IChatbotState> = (
  state: IStoreState["chatbot"] = initialChatbotState,
  action,
) => {
  switch (action.type) {
    case FETCH_CONTACT_LIST_PROGRESS:
      return {
        ...state,
        contactList: {
          ...state.contactList,
          loading: true,
          error: null,
        },
      };

    case FETCH_CONTACT_LIST_SUCCESS:
      return {
        ...state,
        contactList: {
          ...state.contactList,
          loading: false,
          list: [...state.contactList.list, ...action.payload.data],
          totalRecords: action.payload.totalRecords,
          hasMoreRecords: action.payload.data.length > 0,
          message: action.payload.message,
          meta: action.payload.meta,
          error: null,
        },
      };

    case FETCH_CONTACT_LIST_FAILED:
      return {
        ...state,
        contactList: {
          ...state.contactList,
          loading: false,
          error: "Failed to fetch contacts",
          // Don't clear existing data on fetch failure - preserve previous pages
        },
      };

    case FETCH_CONTACT_LIST_WHATSAPP_NOT_SCANNED:
      return {
        ...state,
        contactList: {
          ...state.contactList,
          loading: false,
          error: "WHATSAPP_NOT_SCANNED",
          // Don't clear existing data - preserve previous pages
        },
      };

    case FETCH_CONTACT_LIST_WHATSAPP_SERVICE_OFF:
      return {
        ...state,
        contactList: {
          ...state.contactList,
          loading: false,
          error: "WHATSAPP_SERVICE_OFF",
          // Don't clear existing data - preserve previous pages
        },
      };

    case CLEAR_CONTACT_STATE:
      return {
        ...state,
        contactList: {
          ...initialChatbotState.contactList,
        },
      };

    case FETCH_ACTION_EVENT_PROGRESS: {
      const newState = produce(state, (draftState) => {
        draftState.actionEventList.loading = LoadState.InProgress;
        draftState.actionEventList.list = [];
        draftState.actionEventList.error = null;
      });
      return newState;
    }

    case FETCH_ACTION_EVENT_SUCCESS: {
      const { data, totalRecords } = action.payload;
      const newState = produce(state, (draftState) => {
        draftState.actionEventList.loading = LoadState.Loaded;
        draftState.actionEventList.list = data;
        draftState.actionEventList.totalRecords = totalRecords;
        draftState.actionEventList.hasMoreRecords = data.length > 0;
        draftState.actionEventList.error = null;
      });
      return newState;
    }

    case FETCH_ACTION_EVENT_FAILED: {
      const newState = produce(state, (draftState) => {
        draftState.actionEventList.loading = LoadState.Failed;
        draftState.actionEventList.error = "Failed to fetch action events";
      });
      return newState;
    }

    case FETCH_WHATSAPP_MSG_MEDIA_HISTORY_PROGRESS: {
      const newState = produce(state, (draftState) => {
        draftState.whatsappMsgMediaHistory.loading = LoadState.InProgress;
        draftState.whatsappMsgMediaHistory.list = [];
        draftState.whatsappMsgMediaHistory.error = null;
      });
      return newState;
    }

    case FETCH_WHATSAPP_MSG_MEDIA_HISTORY_SUCCESS: {
      const { data, currentRecords } = action.payload;
      const newState = produce(state, (draftState) => {
        draftState.whatsappMsgMediaHistory.loading = LoadState.Loaded;
        draftState.whatsappMsgMediaHistory.list = data;
        draftState.whatsappMsgMediaHistory.currentRecords = currentRecords;
        draftState.whatsappMsgMediaHistory.error = null;
      });
      return newState;
    }

    case FETCH_WHATSAPP_MSG_MEDIA_HISTORY_FAILED: {
      const newState = produce(state, (draftState) => {
        draftState.whatsappMsgMediaHistory.loading = LoadState.Failed;
        draftState.whatsappMsgMediaHistory.error =
          "Failed to fetch whatsapp messages media history";
      });
      return newState;
    }

    case CLEAR_ACTION_EVENT_STATE: {
      const newState = produce(state, (draftState) => {
        draftState.actionEventList = { ...initialChatbotState.actionEventList };
      });
      return newState;
    }

    default:
      return state;
  }
};

export default chatbotReducer;
